import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { ArrowLeft, RefreshCw } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

import {
  requestLoadAllFiles,
  requestFile,
  requestLinkToDeliverable,
  requestCloseLinkToDeliverable
} from '../../stores/files'

import ProxyPlayer from '../../components/ProxyPlayer'
import LinkToDeliverable from '../../components/LinkToDeliverable'

// A single labelled field in a details card
function Field({ label, value }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div className="border-b px-3 py-1.5 last:border-b-0">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="break-words text-sm font-medium leading-snug">{value}</div>
    </div>
  )
}

function CardHeading({ children }) {
  return (
    <div className="border-b bg-muted/40 px-3 py-1.5">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">{children}</span>
    </div>
  )
}

function View({
  error,
  hasError,
  loading,
  file,
  linkToDeliverable,
  requestFile,
  requestLoadAllFiles,
  requestLinkToDeliverable,
  requestCloseLinkToDeliverable
}) {
  const [showProxyLink, setShowProxyLink] = useState(false)
  const [showReportAnIssue, setShowReportAnIssue] = useState(false)

  useEffect(() => {
    if (!file) requestFile()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDateTime = val =>
    val ? `${new Date(val).toLocaleDateString()} ${new Date(val).toLocaleTimeString()}` : null

  return (
    <div className="space-y-3">
      {/* ── Title row ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={requestLoadAllFiles} title="Back to all files">
          <ArrowLeft className="h-4 w-4" /> All Files
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-sm font-semibold" title={file ? file.fileName : ''}>
            {file ? file.fileName : 'File'}
          </h1>
          {file && file.clientName && (
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">· {file.clientName}</span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowReportAnIssue(true)}>
          Report an Issue
        </Button>
        {file && (
          <Button variant="outline" size="sm" onClick={() => requestLinkToDeliverable()}>
            Download Deliverable
          </Button>
        )}
        {file && file.proxyLink && (
          <Button variant="outline" size="sm" onClick={() => setShowProxyLink(true)}>
            Download Proxy
          </Button>
        )}
        <Button variant="outline" size="icon-sm" onClick={requestFile} disabled={loading} title="Refresh file">
          {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {hasError && <Alert variant="error">{error}</Alert>}

      {!file && loading && (
        <div className="flex items-center gap-2 p-8">
          <Spinner size={20} />
          <span className="text-sm text-muted-foreground">Loading file…</span>
        </div>
      )}

      {file && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
          {/* ── Left: details ── */}
          <div className="flex min-w-0 flex-col gap-3 lg:w-1/2">
            <Card className="overflow-hidden">
              <CardHeading>File Details</CardHeading>
              <Field label="Client" value={file.clientName} />
              <Field label="Program" value={file.programName} />
              <Field label="Filename" value={file.fileName} />
              <Field label="Airing Station(s)" value={file.airingStations} />
              <Field label="Episode Number" value={file.episodeNumber} />
              <Field label="Episode Title" value={file.episodeTitle} />
              <Field label="Delivery To" value={file.deliverTo} />
              <Field label="Delivery Date" value={fmtDateTime(file.deliveryDateTime)} />
            </Card>

            <Card className="overflow-hidden">
              <CardHeading>MediaInfo</CardHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div>
                  <Field label="Duration" value={file.duration} />
                  <Field label="File Size" value={file.size} />
                  <Field label="Definition" value={file.definition} />
                  <Field label="Format" value={file.format} />
                  <Field label="Video Format Profile" value={file.videoFormatProfile} />
                  <Field label="Frame Rate" value={file.frameRate} />
                  <Field label="Video Bitrate" value={file.videoBitRate} />
                  <Field label="Chroma Sampling" value={file.chromaSampling} />
                  <Field label="Audio Bit Depth" value={file.audioBitDepth} />
                </div>
                <div>
                  <Field label="Duration (Timecode)" value={file.durationInTimecode} />
                  <Field label="File Size (Bytes)" value={file.sizeInBytes} />
                  <Field label="Standard" value={file.standard} />
                  <Field label="Video Format" value={file.videoFormat} />
                  <Field label="Video Resolution" value={file.videoHeight ? `${file.videoWidth}x${file.videoHeight}` : null} />
                  <Field label="Aspect Ratio" value={file.videoAspectRatio} />
                  <Field label="Scan Type" value={file.scanType} />
                  <Field label="Audio Sampling Rate" value={file.audioSamplingRate} />
                </div>
              </div>
            </Card>
          </div>

          {/* ── Right: proxy player ── */}
          <Card className="min-w-0 overflow-hidden lg:w-1/2">
            <CardHeading>Proxy Player · {file.fileNameWithoutExtension}</CardHeading>
            {file.proxyLink ? (
              <ProxyPlayer
                proxyLink={file.proxyLink}
                frameRate={file.frameRate}
                aspectRatio={file.videoAspectRatio}
              />
            ) : (
              <div className="p-6 text-sm text-muted-foreground">Proxy not available</div>
            )}
          </Card>
        </div>
      )}

      <LinkToDeliverable
        requestClose={requestCloseLinkToDeliverable}
        sourceLink={file ? file.sourceLink : null}
        show={linkToDeliverable.show}
        hasError={linkToDeliverable.hasError}
        error={linkToDeliverable.error}
        loading={linkToDeliverable.loading}
      />

      {/* Download Proxy dialog */}
      <Dialog open={showProxyLink} onOpenChange={setShowProxyLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Proxy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Note: This is a downloadable link to the low-resolution proxy and is not the deliverable for air.
              The link expires in 7 days.
            </p>
            {file && file.proxyLink && (
              <a
                href={file.proxyLink}
                target="_blank"
                rel="noreferrer"
                className="block break-all font-medium text-primary hover:underline"
              >
                {file.proxyLink}
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProxyLink(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report an Issue dialog */}
      <Dialog open={showReportAnIssue} onOpenChange={setShowReportAnIssue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              For immediate assistance, call our 24-hour emergency line:{' '}
              <a className="text-primary hover:underline" href="tel:9492161063">(949) 216-1063</a>.
            </p>
            <p>
              If your issue is not an emergency, please send an email to{' '}
              <a className="text-primary hover:underline" href="mailto:aberfast@abercap.com">aberfast@abercap.com</a>.
              We'll respond during normal business hours.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportAnIssue(false)}>Close</Button>
            <Button
              onClick={() => {
                const subject = encodeURIComponent(`Aberdeen Hub - Issue with ${file ? file.fileName : ''}`)
                window.location.href = `mailto:aberfast@abercap.com?subject=${subject}&bcc=admin@aberdeen.io`
                setShowReportAnIssue(false)
              }}
            >
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestLoadAllFiles,
    requestFile,
    requestLinkToDeliverable,
    requestCloseLinkToDeliverable
  }, dispatch)
})

const mapStateToProps = (state, route) => {
  const {pathname} = route.location
  const sfidRegex = /\/view\/(.*)/ig
  const match = sfidRegex.exec(pathname)
  const sfid = match ? match[1] : null

  const {
    loading,
    error,
    hasError,
    filesById,
    linkToDeliverable
  } = state.stores.files

  return {
    ...route.match.params,
    loading,
    error,
    hasError,
    file: sfid ? filesById[sfid] : null,
    linkToDeliverable
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(View))
